/* eslint-disable max-len */
import { Box, Grid, List, ListItem, Typography, styled } from '@mui/material';
import Logo from '../../../assets/STATO-INTOO-LOGO-MY-INTOO-VERS-2 A.png';
import ProgramaResumo from './ProgramaResumo';
import CronogramaEtapas from './CronogramaEtapas';
import HistoricoAcoes from './HistoricoReunioes';
import { useTranslation } from '../../../commons/useTranslasion';
import { useDateFormatters } from '../../../utils/useDateFormatters';

interface DetalhesPDFProps {
    comanda: {
        programa: any;
        cronograma: any;
        historico: any[];
    };
}

const DetalhesPDF: React.FC<DetalhesPDFProps> = ({ comanda }) => {
    const { translate } = useTranslation();
    const { formatarData } = useDateFormatters();

    return (
        <GlobalStyle>
            <PDFContainer>
                <PDFContent>
                    <PDFHeader>
                        <HeaderContent>
                            <LogoContainer>
                                <img src={Logo} alt="logo" height="40px" />
                            </LogoContainer>
                            <TitleContainer>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        mt: 1,
                                        color: '#1976d2',
                                        fontWeight: 'bold',
                                        position: 'relative',
                                        display: 'inline-block',
                                        fontSize: '1.1rem',
                                        '&::after': {
                                            content: '""',
                                            position: 'absolute',
                                            bottom: -4,
                                            left: 0,
                                            width: '100%',
                                            height: '2px',
                                            backgroundColor: '#1976d2'
                                        }
                                    }}
                                >
                                    {translate("relatorioIndividualDoProgramaDeTransicaoDeCarreira")}
                                </Typography>
                                <Typography variant="subtitle2" sx={{ mt: 1 }}>
                                    {translate("geradoEm")}: {formatarData(new Date())}
                                </Typography>
                            </TitleContainer>
                        </HeaderContent>
                    </PDFHeader>

                    <PDFSection>
                        <ProgramaResumo programa={comanda.programa} />
                    </PDFSection>

                    <PDFSection>
                        <Grid container spacing={2} sx={{ width: '100% !important' }}>
                            <Grid
                                item
                                xs={8}
                                sx={{
                                    width: '33.333% !important',
                                    maxWidth: '33.333% !important'
                                }}
                            >
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        mt: 1,
                                        color: '#1976d2',
                                        fontWeight: 'bold',
                                        position: 'relative',
                                        display: 'inline-block',
                                        fontSize: '1.1rem',
                                        '&::after': {
                                            content: '""',
                                            position: 'absolute',
                                            bottom: -4,
                                            left: 0,
                                            width: '100%',
                                            height: '2px',
                                            backgroundColor: '#1976d2'
                                        }
                                    }}
                                >
                                    {translate("cronograma")}
                                </Typography>

                                <Box sx={{ padding: 1 }}>
                                    <Box key='example' sx={{ marginBottom: 1 }}>
                                        <List>
                                            <ListItem key='example-list' sx={{ paddingY: 1 }}>
                                                <Grid container>
                                                    <Grid item xs={8}>
                                                        <Typography variant="body1">{translate("etapa")}</Typography>
                                                    </Grid>
                                                    <Grid item xs={4} textAlign="right">
                                                        <Typography variant="body2" color="textSecondary">
                                                            {translate("datas")}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </ListItem>
                                        </List>
                                    </Box>
                                </Box>

                                <CronogramaEtapas cronograma={comanda.cronograma} />
                            </Grid>
                        </Grid>
                    </PDFSection>

                    <PDFSection>
                        <Grid container spacing={2} sx={{ width: '100% !important' }}>
                            <Grid
                                item
                                xs={8}
                                sx={{
                                    width: '66.667% !important',
                                    maxWidth: '66.667% !important'
                                }}
                            >
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        mt: 1,
                                        color: '#1976d2',
                                        fontWeight: 'bold',
                                        position: 'relative',
                                        display: 'inline-block',
                                        fontSize: '1.1rem',
                                        '&::after': {
                                            content: '""',
                                            position: 'absolute',
                                            bottom: -4,
                                            left: 0,
                                            width: '100%',
                                            height: '2px',
                                            backgroundColor: '#1976d2'
                                        }
                                    }}
                                >
                                    {translate("historico")}
                                </Typography>

                                <HistoricoAcoes historico={comanda.historico} />
                            </Grid>
                        </Grid>
                    </PDFSection>

                    <PDFFooter>
                        <Typography variant="body2" sx={{ color: '#666' }}>
                        © {new Date().getFullYear()} STATO - Todos os direitos reservados
                        </Typography>
                    </PDFFooter>
                </PDFContent>
            </PDFContainer>
        </GlobalStyle>
    );
};

const GlobalStyle = styled('div')`
  --green2: #3cb99c;
  --green3: #5cc2aa;
  --blue1: #0288d1;
  --blue2: #1976d2;
  --blue3: #2196f3;
`;

const PDFFooter = styled('div')`
  margin-top: 40px;
  text-align: center;
  padding-top: 20px;
  border-top: 1px solid #e0e0e0;
`;

const PDFContainer = styled('div')`
  background: white;
  width: 297mm;
  min-height: 210mm;
  margin: 0;
  padding: 20px;
`;

const PDFContent = styled('div')`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const PDFHeader = styled('div')`
  width: 100%;
  padding: 20px;
  margin-bottom: 20px;
  background: linear-gradient(
    90deg,
    var(--green2) 0%,
    var(--green2) 20%,
    var(--green3) 40%,
    var(--green3) 60%,
    var(--blue3) 75%,
    var(--blue2) 100%
  );
  transition: width 195ms cubic-bezier(0.4, 0, 0.6, 1), margin 195ms cubic-bezier(0.4, 0, 0.6, 1);
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background: var(--blue2);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
`;

const HeaderContent = styled('div')`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 20px;
`;

const LogoContainer = styled('div')`
  flex: 0 0 auto;
`;

const TitleContainer = styled('div')`
  flex: 1;
  
  .MuiTypography-root {
    color: white !important;
  }
`;

const PDFSection = styled('div')`
  width: 100%;
  
  .MuiGrid-container {
    width: 100%;
    margin: 0;
  }

  .MuiGrid-item {
    box-sizing: border-box;
  }

  // Ajuste específico para o Grid de 4 colunas
  .MuiGrid-item[class*="MuiGrid-grid-xs-4"] {
    width: 33.333%;
    flex: 0 0 33.333%;
  }

  // Ajuste específico para o Grid de 8 colunas
  .MuiGrid-item[class*="MuiGrid-grid-xs-8"] {
    width: 66.667%;
    flex: 0 0 66.667%;
  }
`;

export default DetalhesPDF;